import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Avatar, Box, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase, useFirestore } from 'react-redux-firebase';
import { actions } from '../actions';
import axios from 'axios';
import collections from '../constants/FirestoreCollections';
import { purple } from '@mui/material/colors';
import { AccountCircle, Add } from '@mui/icons-material';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const authUrl = process.env.REACT_APP_AUTH;
const MySwal = withReactContent(Swal);

const useStyles = makeStyles((theme) => ({
  appBarSpacer: theme.mixins.toolbar,
  title: {
    fontSize: theme.sizes.md.t3,
  },
  form: {
    marginTop: theme.spacing(1),
  },
  user: {
    padding: '16px 32px',
    marginBlock: 16,
  },
  userImage: {
    width: 50,
    height: 50,
    borderRadius: '50%',
  },
  adminTag: {
    padding: '2px 16px',
    borderRadius: '3rem',
    background: 'red',
    color: theme.palette.white.main,
    marginRight: 8,
    marginBottom: 4,
    fontSize: 10,
    width: 'fit-content',
  },
  modalPaper: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    '& img': {
      maxHeight: `calc(100dvh - ${theme.spacing(5)})`,
    },
    top: `50%`,
    left: `50%`,
    transform: `translate(-50%, -50%)`,
  },
  submitButtonContainer: {
    marginTop: theme.spacing(3),
  },
  circularProgress: {
    marginLeft: theme.spacing(2),
    verticalAlign: 'middle',
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  dangerButton: {
    color: theme.palette.white.main,
    backgroundColor: theme.palette.danger.main,
    '&:hover': {
      backgroundColor: theme.palette.danger.light,
      opacity: '.8',
    },
  },
  avatar: {
    color: theme.palette.getContrastText(purple[500]),
    backgroundColor: purple[500],
    width: '100%',
    height: '100%',
  },
  avatarIcon: {
    width: '80%',
    height: '80%',
  },
  userImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '3rem',
  },
  addButton: {
    background: '#9C27B0',
    '&:hover': {
      background: '#b93ecf',
    },
    color: theme.palette.white.main,
  },
  addButtonIcon: {
    color: theme.palette.white.main,
  },
}));

export default function Users() {
  const classes = useStyles();

  const firebase = useFirebase();
  const firestore = useFirestore();
  const appDispatch = useDispatch();
  const organization = useSelector((state) => state.app.organization);
  const profile = useSelector((state) => state.firebase.profile);

  const [unsubscribes, setUnsubscribes] = useState([]);
  const [users, setUsers] = useState([]);

  const rolePermissions = {
    admin: ['organizationAdmin', 'none'],
    organizationAdmin: ['none'],
  };

  const roleHierarchy = {
    admin: 2,
    organizationAdmin: 1,
    none: 0, // Sin permisos es un usuario normal
  };

  useEffect(() => {
    if (organization.id) {
      const unsubscribe = firestore
        .collection(collections.users)
        .where('organizationId', '==', organization.id)
        .onSnapshot((res) => {
          const _users = [];
          res.forEach((e) => {
            _users.push({ id: e.id, ...e.data() });
          });
          setUsers(_users);
        });
      if (unsubscribes && unsubscribes.lenght > 0) {
        for (const _unsubscribe of unsubscribes) _unsubscribe();
      }
      setUnsubscribes([unsubscribe]);
    }
  }, [firestore, organization.id]);

  const [newUserEmail, setNewUserEmail] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    appDispatch({
      type: actions.changeLoading,
      loading: { loading: true, priority: 1 },
    });
    firebase
      .auth()
      .currentUser.getIdToken(true)
      .then(function (token) {
        axios(`${authUrl}/user`, {
          method: 'post',
          headers: {
            Authorization: `bearer ${token}`,
          },
          data: {
            email: newUserEmail,
            organizationId: organization.id,
          },
        })
          .then((res) => {
            setNewUserEmail('');
            handleModalClose();
            appDispatch({
              type: actions.alert,
              alert: {
                snackbarOpen: true,
                snackbarMessage: `Se ha enviado una invitación al email ${newUserEmail}`,
                snackbarSeverity: 'info',
              },
            });
          })
          .catch((err) => {
            const status = err.response.status;
            if (status === 400) {
              const message = err.response.data.message;
              appDispatch({
                type: actions.alert,
                alert: {
                  snackbarOpen: true,
                  snackbarMessage: message,
                  snackbarSeverity: 'error',
                },
              });
            } else {
              appDispatch({
                type: actions.alert,
                alert: {
                  snackbarOpen: true,
                  snackbarMessage: 'Un problema ha ocurrido, intente nuevamente mas tarde.',
                  snackbarSeverity: 'error',
                },
              });
            }
          })
          .finally(() => {
            appDispatch({
              type: actions.changeLoading,
              loading: { loading: false, priority: 1 },
            });
          });
      });
  };

  const handleChangePermissions = async (user) => {
    try {
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: true, priority: 1 },
      });

      let isOrganizationAdmin = false;
      if (!user.claims) isOrganizationAdmin = true;
      else if (user.claims && !user.claims.includes('organizationAdmin'))
        isOrganizationAdmin = true;

      firebase
        .auth()
        .currentUser.getIdToken(true)
        .then(function (token) {
          axios(`${authUrl}/claims`, {
            method: 'post',
            headers: {
              Authorization: `bearer ${token}`,
            },
            data: {
              uid: user.id,
              isOrganizationAdmin,
            },
          })
            .then((res) => {
              appDispatch({
                type: actions.alert,
                alert: {
                  snackbarOpen: true,
                  snackbarMessage: '!Usuario actualizado exitosamente!',
                  snackbarSeverity: 'success',
                },
              });
            })
            .catch((err) => {
              console.error('handleChangePermissions', err && err.response);
            })
            .finally(() => {
              appDispatch({
                type: actions.changeLoading,
                loading: { loading: false, priority: 1 },
              });
            });
        });
    } catch (err) {
      console.error('handleChangePermissions', err);
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: false, priority: 1 },
      });
    }
  };

  const handleRemoveUser = (user) => {
    MySwal.fire({
      text: '¿Esta seguro que quiere borrarlo?',
      showConfirmButton: true,
      showDenyButton: true,
      icon: 'error',
      inputValue: true,
    }).then((result) => {
      if (result.isConfirmed) {
        appDispatch({
          type: actions.changeLoading,
          loading: { loading: true, priority: 1 },
        });
        firebase
          .auth()
          .currentUser.getIdToken(true)
          .then(function (token) {
            axios(`${authUrl}/user/${user.id}`, {
              method: 'delete',
              headers: {
                Authorization: `bearer ${token}`,
              },
            })
              .then(() => {
                appDispatch({
                  type: actions.alert,
                  alert: {
                    snackbarOpen: true,
                    snackbarMessage: `Se ha eliminado correctamente al usuario ${newUserEmail}`,
                    snackbarSeverity: 'success',
                  },
                });
              })
              .catch((err) => {
                console.error('handleRemoveUser', err);
                appDispatch({
                  type: actions.alert,
                  alert: {
                    snackbarOpen: true,
                    snackbarMessage: 'Un problema ha ocurrido, intente nuevamente mas tarde.',
                    snackbarSeverity: 'error',
                  },
                });
              })
              .finally(() => {
                appDispatch({
                  type: actions.changeLoading,
                  loading: { loading: false, priority: 1 },
                });
              });
          });
      }
    });
  };

  const canManageRole = (currentUser, targetUser) => {
    const currentUserRole = getHighestRole(currentUser.claims);
    const targetUserRole = getHighestRole(targetUser.claims);

    return rolePermissions[currentUserRole]?.includes(targetUserRole);
  };

  const getHighestRole = (roles) => {
    if (!roles || roles.length === 0) return 'none';

    return roles.reduce(
      (highest, role) => (roleHierarchy[role] > roleHierarchy[highest] ? role : highest),
      'none',
    );
  };

  return (
    <Box>
      <Box display="flex">
        <Box display="flex" alignItems="center">
          <Button className={classes.addButton} variant="contained" onClick={handleModalOpen}>
            <Add className={classes.addButtonIcon} />
            Añadir usuario
          </Button>
        </Box>
      </Box>
      <Box>
        {users.map((user) => (
          <Paper key={user.id} className={classes.user}>
            <Box display="flex">
              <Box display="flex" flexDirection="column" alignItems="center" mr={2}>
                <Box className={classes.userImage}>
                  {user && user.image ? (
                    <img className={classes.userImg} src={user.image} />
                  ) : (
                    <Avatar className={classes.avatar}>
                      <AccountCircle className={classes.avatarIcon} />
                    </Avatar>
                  )}
                </Box>
              </Box>
              <Box display="flex" flexDirection="column">
                {getHighestRole(user?.claims) === 'admin' && (
                  <Typography className={classes.adminTag}>Admin - prestobots</Typography>
                )}
                {getHighestRole(user?.claims) === 'organizationAdmin' && (
                  <Typography className={classes.adminTag}>Admin</Typography>
                )}
                {user.firstName && user.lastName && (
                  <Typography>
                    {user.firstName} {user.lastName}
                  </Typography>
                )}
                {user.email && <Typography>{user.email}</Typography>}
              </Box>
              {canManageRole(profile, user) && (
                <Box display="flex" alignItems="center" ml="auto">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleChangePermissions(user)}
                  >
                    {user.claims?.includes('organizationAdmin')
                      ? 'Quitar permisos'
                      : 'Añadir permisos'}
                  </Button>
                  <Box ml={2}>
                    <Button
                      variant="contained"
                      className={classes.dangerButton}
                      onClick={() => handleRemoveUser(user)}
                    >
                      Eliminar usuario
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
      <Modal open={modalOpen} onClose={handleModalClose} aria-labelledby="simple-modal-title">
        <Box className={classes.modalPaper}>
          <h2 id="simple-modal-title">Agregar Usuario</h2>
          <form className={classes.form} onSubmit={handleSubmit}>
            <Box>
              <TextField
                type="email"
                id="new-user-email"
                label="Email"
                variant="outlined"
                value={newUserEmail}
                onChange={(event) => setNewUserEmail(event.target.value)}
                required
              />
            </Box>
            <Box display="flex" className={classes.submitButtonContainer}>
              <Box mr={1}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    setModalOpen(false);
                  }}
                >
                  Cerrar
                </Button>
              </Box>
              <Box ml={1}>
                <Button type="submit" variant="contained" color="primary">
                  Confirmar
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>
    </Box>
  );
}
